import Icon from './icons/Icon'
import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import type { ConversationSummary } from '../services/conversations'
import { fetchConversations, archiveConversation } from '../services/conversations'
import '../styles/app.css'

interface NavLink {
  label: string
  to: string
}

interface ConversationSidebarProps {
  activeConversationId: number | null
  onSelectConversation: (conversationId: number) => void
  onNewConversation: () => void
  isOpen: boolean
  onClose: () => void
  isCollapsed: boolean
  onToggleCollapse: () => void
  navigationLinks?: NavLink[]
  userName?: string
  userEmail?: string
  onLogout?: () => void
}

const ConversationSidebar: React.FC<ConversationSidebarProps> = ({
  activeConversationId,
  onSelectConversation,
  onNewConversation,
  isOpen,
  onClose,
  isCollapsed,
  onToggleCollapse,
  navigationLinks,
  userName,
  userEmail,
  onLogout,
}) => {
  const [conversations, setConversations] = useState<ConversationSummary[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const location = useLocation()

  const loadConversations = async (): Promise<void> => {
    try {
      setIsLoading(true)
      setError(null)
      const data = await fetchConversations(20, false)
      setConversations(data)
    } catch (err) {
      console.error('Error loading conversations:', {
        message: err instanceof Error ? err.message : 'Unknown error',
        stack: err instanceof Error ? err.stack : undefined,
        context: { err },
      })
      setError('Failed to load conversations')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadConversations()
  }, [])

  useEffect(() => {
    if (isOpen) {
      loadConversations()
    }
  }, [isOpen])

  const handleArchive = async (
    conversationId: number,
    event: React.MouseEvent
  ): Promise<void> => {
    event.stopPropagation()
    try {
      await archiveConversation(conversationId)
      setConversations((prev) =>
        prev.filter((c) => c.id !== conversationId)
      )
    } catch (err) {
      console.error('Error archiving conversation:', {
        message: err instanceof Error ? err.message : 'Unknown error',
        stack: err instanceof Error ? err.stack : undefined,
        context: { conversationId, err },
      })
    }
  }

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffDays === 0) {
      return 'Today'
    } else if (diffDays === 1) {
      return 'Yesterday'
    } else if (diffDays < 7) {
      return `${diffDays} days ago`
    } else {
      return date.toLocaleDateString()
    }
  }

  const truncateTitle = (title: string, maxLength: number = 30): string => {
    if (title.length <= maxLength) return title
    return title.substring(0, maxLength) + '...'
  }

  const closeMobileOnly = (): void => {
    if (window.innerWidth < 769) onClose()
  }

  const handleSelect = (conversationId: number): void => {
    onSelectConversation(conversationId)
    closeMobileOnly()
  }

  const handleNew = (): void => {
    onNewConversation()
    closeMobileOnly()
  }

  return (
    <>
      {isOpen && (
        <div className="sidebar-backdrop" onClick={onClose} />
      )}

      <div className={`conversation-sidebar ${isOpen ? 'open' : ''} ${isCollapsed ? 'collapsed' : ''}`}>
        {/* Header */}
        <div className="sidebar-header">
          {!isCollapsed && <h3>Vault</h3>}
          <button
            className="sidebar-toggle"
            onClick={onClose}
            title="Close sidebar"
          >
            <span className="toggle-icon">&#x2715;</span>
          </button>
          <button
            className="sidebar-collapse-btn"
            onClick={onToggleCollapse}
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <line x1="9" y1="3" x2="9" y2="21" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <line x1="9" y1="3" x2="9" y2="21" />
              </svg>
            )}
          </button>
        </div>

        <button
          className="new-conversation-btn"
          onClick={handleNew}
        >
          <Icon name="plus" />New Chat
        </button>

        {/* Navigation links */}
        {navigationLinks && navigationLinks.length > 0 && (
          <div className="sidebar-nav-links">
            {navigationLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`sidebar-nav-item ${location.pathname === link.to ? 'active' : ''}`}
                onClick={closeMobileOnly}
              >
                {link.label}
              </Link>
            ))}
          </div>
        )}

        {/* Conversations list */}
        <div className="sidebar-section-label">Recent Chats</div>
        <div className="conversations-list">
          {isLoading && (
            <div className="sidebar-loading">Loading...</div>
          )}

          {error && (
            <div className="sidebar-error">{error}</div>
          )}

          {!isLoading && !error && conversations.length === 0 && (
            <div className="sidebar-empty">
              No conversations yet. Start a new chat!
            </div>
          )}

          {conversations.map((conversation) => (
            <div
              key={conversation.id}
              className={`conversation-item ${
                conversation.id === activeConversationId ? 'active' : ''
              }`}
              onClick={() => handleSelect(conversation.id)}
            >
              <div className="conversation-title">
                {truncateTitle(conversation.title || `Chat ${conversation.id}`)}
              </div>
              <div className="conversation-meta">
                <span className="conversation-date">
                  {formatDate(conversation.updated_at)}
                </span>
                <span className="conversation-count">
                  {conversation.message_count} messages
                </span>
              </div>
              <button
                className="archive-btn"
                onClick={(e) => handleArchive(conversation.id, e)}
                title="Archive conversation"
              >
                &times;
              </button>
            </div>
          ))}
        </div>

        {/* Footer — user info + logout */}
        {(userName || onLogout) && (
          <div className="sidebar-footer">
            {userName && (
              <div className="sidebar-user-info">
                <div className="sidebar-user-avatar">
                  {userName.charAt(0).toUpperCase()}
                </div>
                <div className="sidebar-user-details">
                  <div className="sidebar-user-name">{userName}</div>
                  {userEmail && <div className="sidebar-user-email">{userEmail}</div>}
                </div>
              </div>
            )}
            {onLogout && (
              <>
                <Link
                  to="/account"
                  className="sidebar-account-link"
                  onClick={closeMobileOnly}
                >
                  Manage Account
                </Link>
                <button
                  className="sidebar-logout-btn"
                  onClick={() => { onLogout(); closeMobileOnly() }}
                >
                  Log out
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </>
  )
}

export default ConversationSidebar
